/**
 * A startup card on the pipeline board.
 *
 * Moving a card is drag-and-drop, and only drag-and-drop: the earlier
 * per-card "Move to stage" select is gone. A dropdown on every card put a
 * 44px control on every tile, which crowded the tile down to its name and
 * pushed the column past a screen height with four cards in it.
 *
 * PRD §9.7's keyboard alternative is still satisfied, by dnd-kit's own
 * keyboard dragging rather than by a parallel control: the drag handle is a
 * real `<button>`, so Tab reaches it, Space picks the card up, the arrow keys
 * move it between columns and Space drops it. `buildAnnouncements` narrates
 * each of those steps by startup and stage name, and the board renders the
 * instructions for anyone who focuses a handle. One path, described out loud,
 * beats two paths where the second exists only to be accessible.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md tasks 6.1, 6.5.9, PRD §9.7
 */
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';

import type { StartupSummary } from '@nexus/contracts';

import { BandBadge } from '../../../components/domain/BandBadge';
import { Badge } from '../../../components/ui/Badge';
import { Icon } from '../../../components/ui/Icon';
import { cn } from '../../../lib/cn';
import { formatNumber } from '../../../lib/format';

export interface BoardCardProps {
  startup: StartupSummary;
  onOpen: (id: string) => void;
  /**
   * Whether this user may move cards. A viewer's card is not draggable and
   * shows no handle — an affordance that always snaps back reads as a broken
   * board rather than as a permission boundary.
   */
  canEdit: boolean;
}

export function BoardCard({
  startup,
  onOpen,
  canEdit,
}: BoardCardProps): React.JSX.Element {
  const { t } = useTranslation();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id: startup.id,
      /* Disabled for a viewer: dragging a card they cannot move would show it
         land and then snap back, which reads as a broken board rather than as
         a permission boundary. */
      disabled: !canEdit,
    });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        /* The prototype's tile: a hairline border that turns blue and gains a
           shadow on hover, rather than a permanently elevated card. Inside an
           already-elevated column, tiles that also cast shadows read as a pile
           rather than as a list. */
        'border-gray-100 hover:border-blue flex flex-col gap-2 rounded-md',
        'border bg-white p-3 transition-[border-color,box-shadow] hover:shadow-sm',
        /* A drag that begins on the handle still travels across the card; left
           selectable, it paints a text selection over the board as it goes. */
        'select-none',
        /* `isDragging` is dnd-kit drag state, set from element geometry jsdom
           does not compute, so it is unreachable here and verified in a
           browser. */
        /* v8 ignore next */
        isDragging ? 'opacity-50' : '',
      )}
    >
      <div className="flex items-start gap-2">
        {/* A real button, so the name is Tab-reachable and Enter-openable. */}
        <button
          type="button"
          onClick={() => {
            onOpen(startup.id);
          }}
          className="text-navy-text flex-1 text-left text-sm font-semibold"
        >
          {startup.name}
        </button>

        {canEdit ? (
          /* The drag handle carries dnd-kit's own listeners and ARIA. It is a
             button so it is focusable, which is what makes keyboard dragging
             reachable at all. */
          <button
            type="button"
            {...attributes}
            {...listeners}
            /* Named with the startup, because a board of nine columns has one
               of these per card and "Drag" alone identifies none of them. */
            aria-label={t('board.dragHandle', { name: startup.name })}
            className={cn(
              'text-gray-400 hover:text-blue hover:bg-gray-100 grid size-11 flex-none',
              'cursor-grab place-items-center rounded-md active:cursor-grabbing',
              /* `touch-none` so a finger drag moves the card instead of
                 scrolling the board — without it a touch drag never reaches
                 dnd-kit at all. `select-none` so a pointer drag does not
                 sweep a text selection across the card instead. Both are
                 verified in a browser; jsdom computes neither. */
              'touch-none select-none',
            )}
          >
            <Icon name="arrow" aria-hidden />
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <BandBadge band={startup.priorityBand} />
        {startup.isStalled ? <Badge tone="warn">{t('startup.stalled')}</Badge> : null}
        <span className="text-gray-400 text-xs tabular-nums">
          {formatNumber(startup.daysInStage)}
        </span>
      </div>
    </li>
  );
}
